/**
 * Human display names for ledger `kind` and `source_plan` values, in two layers:
 *   1. Admin overrides — `tenant_config.features.ledger_labels` /
 *      `ledger_source_plan_labels` (sparse: only the entries an admin renamed).
 *   2. Built-in defaults below — nice names for every known kind/plan.
 * Anything still unmatched falls back to a snake_case → Title Case humanization,
 * so a kind minted by a future plan never renders blank or raw.
 *
 * The override maps default to `{}` in TENANT_FEATURE_DEFAULTS; the non-empty
 * built-ins live here so member + admin share one source of truth.
 */

export const LEDGER_KIND_LABEL_DEFAULTS: Record<string, string> = {
  // Commissions & bonuses
  direct: 'Direct Referral',
  indirect: 'Indirect Referral',
  unilevel_commission: 'Unilevel Commission',
  realtime_unilevel_cashback: 'Realtime Unilevel Cashback',
  unilevel_consecutive_bonus: 'Unilevel Consecutive Bonus',
  binary_pairing: 'Binary Pairing',
  binary_pairing_gc: 'Binary Pairing (GC)',
  stairstep_commission: 'Stairstep Commission',
  stairstep_rankup_bonus: 'Stairstep Rank-Up Bonus',
  stairstep_rankup_bonus_gc: 'Stairstep Rank-Up Bonus (GC)',
  australian_pass_up: 'Australian Pass-Up',
  mentor_bonus: 'Mentor Bonus',
  monthly_direct_bonus: 'Monthly Direct Bonus',
  monthly_direct_mentor_bonus: 'Monthly Direct Mentor Bonus',
  infinity_bonus: 'Infinity Bonus on Activation',
  give_me: 'Give Me Bonus',
  dropshipping_income: 'Dropshipping Income',
  maintenance_cashback: 'Cashback Bonus',
  sign_up_bonus: 'Sign-Up Bonus',
  sign_up_bonus_gc: 'Sign-Up Bonus (GC)',
  sign_up_vortex: 'Sign-Up Vortex',
  // Pair-drop riders on binary (cash + GC milestone bonuses)
  binary_pair_cash: 'Binary Pair Cash Bonus',
  binary_pair_gc: 'Binary Pair GC Bonus',
  // Mentor rider on binary pairing (sponsor-tree upline % of a downline's binary cash)
  binary_mentor_bonus: 'Binary Mentor Bonus',
  // Coins & tokens
  coin_grant: 'Coin Grant',
  coin_grant_activation: 'Coin Grant (Activation)',
  coin_reward: 'Coin Reward',
  // AI Assistant credits
  ai_credit_grant: 'AI Credits (Product Reward)',
  ai_credit_reward: 'AI Credits Earned',
  ai_credit_topup: 'AI Credits Purchased',
  ai_credit_spend: 'AI Credit Used',
  vortex_token_grant: 'Vortex Token Grant',
  vortex_direct_token: 'Vortex Direct-Recruit Token',
  vortex_token_spend: 'Vortex Token Spent',
  vortex_reentry_token: 'Vortex Re-entry Token',
  vortex_reward: 'Vortex Reward',
  vortex_reward_gc: 'Vortex Reward (GC)',
  vortex_downline_cycle: 'Vortex Downline Cycle',
  vortex_upline_cycle: 'Vortex Upline Cycle',
  // CD / Half-CD debt garnish (paired debit riding the earning plan's event)
  cd_debt_payment: 'CD Debt Payment',
  // Payouts
  payout_hold: 'Payout Hold',
  payout_cancel_refund: 'Payout Cancelled (Refund)',
  payout_reject_refund: 'Payout Rejected (Refund)',
  // Wallet spend (concrete; the dynamic `<cur>_spend` family is handled below)
  cash_spend: 'Cash Spent',
  gc_spend: 'GC Spent',
  coin_spend: 'Coins Spent',
  // Binary internal leg-PV log
  contribution: 'Leg PV Contribution',
  pair_consumed: 'Pair Consumed',
  flushout: 'Strong-Leg Flushout',
};

export const LEDGER_SOURCE_PLAN_LABEL_DEFAULTS: Record<string, string> = {
  direct: 'Direct Referral',
  indirect: 'Indirect Referral',
  unilevel: 'Unilevel',
  realtime_unilevel: 'Realtime Unilevel',
  binary: 'Binary',
  stairstep: 'Stairstep',
  australian_pass_up: 'Australian Pass-Up',
  mentor_bonus: 'Mentor Bonus',
  monthly_direct_bonus: 'Monthly Direct Bonus',
  unilevel_consecutive_bonus: 'Unilevel Consecutive Bonus',
  infinity_bonus: 'Infinity Bonus on Activation',
  give_me: 'Give Me Bonus',
  dropshipping_income: 'Dropshipping Income',
  maintenance_cashback: 'Cashback Bonus',
  sign_up_bonus: 'Sign-Up Bonus',
  sign_up_vortex: 'Sign-Up Vortex',
  vortex: 'Vortex',
  coin_grant: 'Coin Grant',
  payout: 'Payout',
  // Snapshot balances carried over by the legacy data import (not earned here).
  import: 'Initial Balance',
};

const CURRENCY_SPEND_LABEL: Record<string, string> = {
  cash: 'Cash Spent',
  gc: 'GC Spent',
  coin: 'Coins Spent',
  token: 'Tokens Spent',
  ai_credit: 'AI Credit Used',
};

/**
 * Display label for a slot lifecycle `status`. There is no pending state in the
 * product: a slot is either active (created+activated by an admin, or activated
 * via Code+PIN redemption) or it doesn't exist. `pending_payment` is a retired
 * enum value kept only so any legacy row renders a sane label.
 */
export const SLOT_STATUS_LABELS: Record<string, string> = {
  pending_payment: 'Inactive (legacy)',
  active: 'Active',
  suspended: 'Suspended',
  cancelled: 'Cancelled',
};

export function slotStatusLabel(status: string): string {
  return SLOT_STATUS_LABELS[status] ?? humanizeToken(status);
}

/** snake_case / kebab-case → Title Case. The final fallback for unknown values. */
export function humanizeToken(s: string): string {
  return s
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

/**
 * Display label for a ledger `kind`. Resolution order:
 *   admin override → built-in default → dynamic family → humanized fallback.
 * Dynamic families minted at runtime by currency:
 *   `<cur>_spend`   → "<Cur> Spent"
 *   `<base>_refund` → "Refund — <base label>"
 */
export function labelizeLedgerKind(kind: string, labels?: Record<string, string> | null): string {
  const override = labels?.[kind];
  if (override) return override;
  const builtin = LEDGER_KIND_LABEL_DEFAULTS[kind];
  if (builtin) return builtin;
  const spendMatch = /^([a-z]+)_spend$/.exec(kind);
  if (spendMatch && CURRENCY_SPEND_LABEL[spendMatch[1]!]) {
    return CURRENCY_SPEND_LABEL[spendMatch[1]!]!;
  }
  if (kind.endsWith('_refund')) {
    const base = kind.slice(0, -'_refund'.length);
    const baseLabel = labels?.[base] ?? LEDGER_KIND_LABEL_DEFAULTS[base] ?? humanizeToken(base);
    return `Refund — ${baseLabel}`;
  }
  // Reversal rows (admin corrections that debit a prior credit) mirror the
  // `_refund` pattern: `binary_pairing_reversal` → "Reversal — Binary Pairing".
  // Leading with "Reversal" keeps the important word visible in narrow tables.
  if (kind.endsWith('_reversal')) {
    const base = kind.slice(0, -'_reversal'.length);
    const baseLabel = labels?.[base] ?? LEDGER_KIND_LABEL_DEFAULTS[base] ?? humanizeToken(base);
    return `Reversal — ${baseLabel}`;
  }
  return humanizeToken(kind);
}

/**
 * Display label for a ledger entry, preferring an entry-specific name carried in
 * its metadata over the generic `kind` label. Today only the Give Me plan does
 * this: each GM Settings block has its own admin-set title (e.g. "Give Me 4"),
 * stamped onto every `give_me`/`give_me_gc` entry as `metadata.setting_label`, so
 * the wallet logs can distinguish which milestone paid instead of showing the
 * one generic "Give Me Bonus". Falls back to {@link labelizeLedgerKind}.
 */
export function labelizeLedgerEntry(
  kind: string,
  metadata: Record<string, unknown> | null | undefined,
  labels?: Record<string, string> | null,
): string {
  if (kind === 'give_me' || kind === 'give_me_gc') {
    const settingLabel = metadata?.setting_label;
    if (typeof settingLabel === 'string' && settingLabel.trim() !== '') {
      // GC entries keep a "(GC)" suffix so cash vs GC stays distinguishable.
      return kind === 'give_me_gc' ? `${settingLabel} (GC)` : settingLabel;
    }
  }
  return labelizeLedgerKind(kind, labels);
}

/**
 * Display label for a ledger `source_plan`. `<plan>_refund` reversals render as
 * "<plan label> (Refund)".
 */
export function labelizeSourcePlan(plan: string, labels?: Record<string, string> | null): string {
  const override = labels?.[plan];
  if (override) return override;
  if (plan.endsWith('_refund')) {
    const base = plan.slice(0, -'_refund'.length);
    const baseLabel =
      labels?.[base] ?? LEDGER_SOURCE_PLAN_LABEL_DEFAULTS[base] ?? humanizeToken(base);
    return `${baseLabel} (Refund)`;
  }
  return LEDGER_SOURCE_PLAN_LABEL_DEFAULTS[plan] ?? humanizeToken(plan);
}

/**
 * The Give Me plan's display name for THIS tenant, for the member dashboard
 * card heading.
 *
 * The plan is renameable from two unrelated admin surfaces, and a tenant
 * typically only touches one of them:
 *   a) Settings → Ledger labels (`ledger_labels.give_me` /
 *      `ledger_source_plan_labels.give_me`) — renames the wallet/passbook rows.
 *   b) ComPlan → Give Me → each GM Setting's own label — renames the milestone
 *      rows on the dashboard card (`metadata.setting_label` / `settingLabel`).
 * A tenant that renamed the plan to "Hustle Bonus" via (b) alone still saw the
 * card heading read "Give Me Bonus", because the heading only ever consulted
 * (a). This resolves both, in precedence order:
 *
 *   1. an EXPLICIT `ledger_labels.give_me` override
 *   2. an EXPLICIT `ledger_source_plan_labels.give_me` override
 *   3. the GM Settings' shared label, when every labelled setting agrees on one
 *      name (that's the tenant naming the plan itself, not an individual
 *      milestone) — ignoring the editor's auto-generated "Give Me <n>" seeds
 *   4. the built-in default
 *
 * Rungs 1–2 read the raw override maps rather than calling
 * {@link labelizeLedgerKind} / {@link labelizeSourcePlan}, because those return
 * the built-in `'Give Me Bonus'` for an unset key and would mask rung 3
 * entirely. This adds NO third label store — it only reads the two existing
 * override maps plus the plan's own config (same rule as
 * `earningsBreakdownRowLabel`).
 */
export function resolveGiveMePlanLabel(args: {
  /** `tenant_config.features.ledger_labels` */
  kindLabels?: Record<string, string> | null;
  /** `tenant_config.features.ledger_source_plan_labels` */
  planLabels?: Record<string, string> | null;
  /** Every GM Setting's `label`, in config order. */
  settingLabels?: Array<string | null | undefined>;
}): string {
  const explicit = args.kindLabels?.give_me ?? args.planLabels?.give_me;
  if (typeof explicit === 'string' && explicit.trim() !== '') return explicit.trim();

  const labels = (args.settingLabels ?? [])
    .map((l) => (typeof l === 'string' ? l.trim() : ''))
    .filter((l) => l !== '');
  // "Give Me 1", "Give Me 2", … are seeded by the ComPlan editor when a setting
  // is added, so they signal "never renamed", not a tenant-chosen plan name.
  const shared = labels.length > 0 && labels.every((l) => l === labels[0]) ? labels[0]! : null;
  if (shared && !/^give me \d+$/i.test(shared)) return shared;

  return LEDGER_KIND_LABEL_DEFAULTS.give_me!;
}

/** Ordered groups of ledger `kind`s, for the admin label editor. */
export const LEDGER_KIND_GROUPS: Array<{ label: string; kinds: string[] }> = [
  {
    label: 'Commissions & bonuses',
    kinds: [
      'direct',
      'indirect',
      'unilevel_commission',
      'realtime_unilevel_cashback',
      'unilevel_consecutive_bonus',
      'binary_pairing',
      'binary_pair_cash',
      'binary_mentor_bonus',
      'stairstep_commission',
      'australian_pass_up',
      'mentor_bonus',
      'monthly_direct_bonus',
      'monthly_direct_mentor_bonus',
      'infinity_bonus',
      'give_me',
      'sign_up_bonus',
      'sign_up_vortex',
    ],
  },
  {
    label: 'Coins, tokens & GC',
    kinds: [
      'binary_pairing_gc',
      'binary_pair_gc',
      'sign_up_bonus_gc',
      'coin_grant',
      'coin_grant_activation',
      'coin_reward',
      'vortex_token_grant',
      'vortex_direct_token',
      'vortex_token_spend',
      'vortex_reentry_token',
      'vortex_reward',
      'vortex_reward_gc',
      'vortex_downline_cycle',
      'vortex_upline_cycle',
      'ai_credit_grant',
      'ai_credit_reward',
      'ai_credit_topup',
    ],
  },
  {
    label: 'Payouts & spending',
    kinds: [
      'payout_hold',
      'payout_cancel_refund',
      'payout_reject_refund',
      'cash_spend',
      'gc_spend',
      'coin_spend',
      'ai_credit_spend',
    ],
  },
];

/** Source-plan codes shown in the admin label editor. */
export const LEDGER_SOURCE_PLAN_CODES: string[] = Object.keys(LEDGER_SOURCE_PLAN_LABEL_DEFAULTS);
